import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import { useAuth } from '../../contexts/AuthContext';
import { loginWithGoogle, loginWithEmailPassword } from '../../auth';
import styled from 'styled-components';

function LoginPage() {
    const navigate = useNavigate();
    const { user, setUser, isAdmin, loading } = useAuth();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!loading && user) {
            navigate(isAdmin ? '/admin/home' : '/student/home');
        }
    }, [user, isAdmin, loading, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEmailLogin = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        try {
            const loggedInUser = await loginWithEmailPassword(formData.email, formData.password);
            setUser(loggedInUser);

        } catch (error) {
            console.error('Login error:', error.message);
            setErrors({ general: error.message || 'Login failed. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleLogin = async (e) => {
        e.preventDefault();
        try {
            const loggedInUser = await loginWithGoogle();
            setUser(loggedInUser);
        } catch (error) {
            console.error(error.message);
            setErrors({ general: error.message || 'Google login failed. Please try again.' });
        }
    };

    const navigateToRegister = () => {
        navigate('/register');
    };

    return (
        <div className="login-container">
            <div className="login-body">
                <div className="login-image-section">
                    <img src="/images/UCI_Donald_Bren_Hall.jpg" alt="Donald Bren Hall" />
                </div>
                <div className="login-form-section">
                    <div className="login-content">
                        {/* TeamCAST Branding */}
                        <div className="login-brand">
                            <div className="brand-icon">
                                {/* can add a logo */}
                            </div>
                            <div className="brand-text">
                                <span className="brand-name">TeamCAST</span>
                            </div>
                        </div>

                        <p className="login-subtitle">Cooperate with your teammate from today.</p>

                        {/* Email/Password Login Form */}
                        <form onSubmit={handleEmailLogin} className="login-form">
                            <div className="form-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`form-input ${errors.email ? 'error' : ''}`}
                                    placeholder=""
                                />
                                {errors.email && <span className="error-message">{errors.email}</span>}
                            </div>

                            <div className="form-group">
                                <div className="password-label-row">
                                    <label htmlFor="password">Password</label>
                                    <button 
                                        type="button"
                                        onClick={() => navigate('/reset-password')}
                                        className="forgot-password-link"
                                    >
                                        Forgot password?
                                    </button>
                                </div>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className={`form-input ${errors.password ? 'error' : ''}`}
                                    placeholder=""
                                />
                                {errors.password && <span className="error-message">{errors.password}</span>}
                            </div>

                            {errors.general && <div className="error-message general-error">{errors.general}</div>}

                            <button
                                type="submit"
                                className="login-button"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Logging in...' : 'Login to TeamCAST'}
                            </button>
                        </form>

                        <div className="divider">
                            <span>or</span>
                        </div>

                        {/* Social Login */}
                        <div className="social-login">
                            <p className="social-login-text">Login with...</p>
                            <button onClick={handleGoogleLogin} className="google-login-btn">
                                <img src="/images/google.svg" alt="Google" />
                            </button>
                        </div>

                        {/* Sign Up Section */}
                        <div className="signup-section">
                            <p>Don't have an account? <button onClick={navigateToRegister} className="signup-link">Sign Up here</button></p>
                        </div>

                        {/* Help Section */}
                        <HelpSection>
                            <HelpText>
                                Need help?{" "}
                                <HelpButton
                                    onClick={() => {
                                        window.location.href =
                                            "mailto:jennyc28@ics.uci.edu?subject=TeamCAST%20Support%20Request";
                                    }}
                                >
                                    Contact IT Support
                                </HelpButton>
                            </HelpText>

                            <HelpText>
                                First time using TeamCAST?{" "}
                                <HelpButton
                                    onClick={() => {
                                        window.open(
                                            "https://docs.google.com/document/d/10ktlofLuFkFbs9ZEEs2jwpvycE6nz8KBXZyE4FJ8w40/edit?usp=sharing",
                                            "_blank"
                                        );
                                    }}
                                >
                                    View Tutorial
                                </HelpButton>
                            </HelpText>


                            <HelpTextSmall>
                                By signing in you agree with our{" "}
                                <SmallHelpButton
                                    onClick={() => navigate("/terms-of-service")}
                                >
                                    terms and conditions
                                </SmallHelpButton>
                                {" "}and{" "}
                                <SmallHelpButton
                                    onClick={() => navigate("/privacy-policy")}
                                >
                                    privacy policy
                                </SmallHelpButton>
                                .
                            </HelpTextSmall>
                        </HelpSection>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LoginPage;

const HelpSection = styled.div`
  text-align: center;
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.6;
  margin-top: 16px;
`;

const HelpText = styled.p`
  margin-bottom: 6px;
`;

const HelpTextSmall = styled.p`
  margin-top: 18px;
  font-size: 10.5px;
  color: #9ca3af;
`;

const HelpButton = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  font-weight: 500;
  font-size: inherit;
  cursor: pointer;
  text-decoration: none;
  font-family: inherit;
  padding: 0 3px;

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const SmallHelpButton = styled(HelpButton)`
  font-size: inherit;       /* smaller text */
  padding: 0;
`;

